import PropTypes from "prop-types";

export function StatusBadge({ children, tone = "slate" }) {
  const tones = {
    cyan: "border-cyan-300/30 bg-cyan-300/10 text-cyan-100",
    emerald: "border-emerald-300/30 bg-emerald-300/10 text-emerald-100",
    amber: "border-amber-300/30 bg-amber-300/10 text-amber-100",
    rose: "border-rose-300/30 bg-rose-300/10 text-rose-100",
    slate: "border-white/15 bg-white/[0.08] text-slate-200",
  };

  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
}

export function ActionCard({ icon, title, description, onClick, tag }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group min-h-[150px] rounded-lg border border-white/10 bg-white/[0.055] p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-cyan-300/35 hover:bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-cyan-300/40"
    >
      <div className="flex items-start justify-between gap-4">
        <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-cyan-300/12 text-cyan-200 transition group-hover:bg-cyan-300/18">
          {icon}
        </span>
        {tag && <StatusBadge tone="cyan">{tag}</StatusBadge>}
      </div>
      <h3 className="mt-5 text-base font-semibold text-white">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-300">{description}</p>
    </button>
  );
}

export function DestinationCard({ country, stats, courses, accent, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group rounded-lg border border-white/10 bg-slate-900/80 p-5 text-left transition hover:-translate-y-0.5 hover:border-white/25 focus:outline-none focus:ring-2 focus:ring-cyan-300/40"
    >
      <div className="flex items-center justify-between">
        <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${accent} text-lg font-bold text-white`}>
          {country.slice(0, 2).toUpperCase()}
        </div>
        <span className="text-xs font-semibold text-cyan-200">View</span>
      </div>
      <h3 className="mt-5 text-xl font-semibold text-white">{country}</h3>
      <p className="mt-2 text-sm text-slate-300">{stats}</p>
      <p className="mt-4 text-xs uppercase tracking-[2px] text-slate-500">Popular courses</p>
      <p className="mt-1 text-sm text-slate-200">{courses}</p>
    </button>
  );
}

export function OfferCard({ title, consultancy, destination, expiry, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-lg border border-white/10 bg-white/[0.055] p-5 text-left transition hover:-translate-y-0.5 hover:border-amber-300/35 hover:bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-amber-300/30"
    >
      <StatusBadge tone="amber">{destination || "Multi-destination"}</StatusBadge>
      <h3 className="mt-4 text-lg font-semibold text-white">{title}</h3>
      <p className="mt-2 text-sm text-slate-300">{consultancy || "AbroadJacks partner"}</p>
      <p className="mt-4 text-xs text-slate-400">{expiry || "Validity shown when consultancy publishes offer dates"}</p>
    </button>
  );
}

export function LoadingSkeleton({ rows = 3 }) {
  return (
    <div className="grid gap-4">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="h-44 animate-pulse rounded-lg border border-white/10 bg-white/[0.06]" />
      ))}
    </div>
  );
}

StatusBadge.propTypes = {
  children: PropTypes.node.isRequired,
  tone: PropTypes.string,
};

ActionCard.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  tag: PropTypes.string,
};

DestinationCard.propTypes = {
  country: PropTypes.string.isRequired,
  stats: PropTypes.string.isRequired,
  courses: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

OfferCard.propTypes = {
  title: PropTypes.string.isRequired,
  consultancy: PropTypes.string,
  destination: PropTypes.string,
  expiry: PropTypes.string,
  onClick: PropTypes.func,
};

LoadingSkeleton.propTypes = {
  rows: PropTypes.number,
};
